import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import { toast } from 'react-toastify';

import {
  apiEndPoints,
} from '../../../../hooks/remote/apiEndPoints';

const EndpointMonitorDetails = () => {
  document.title =
    'Endpoint Monitor Details - Cloudvantage';

  const navigate = useNavigate();

  const {
    id: projectId,
    monitorId,
  } = useParams();

  // ============================================================
  // ACTIVE TAB
  // ============================================================

  const [
    activeTab,
    setActiveTab,
  ] = useState('overview');

  // ============================================================
  // MONITOR DETAILS
  // ============================================================

  const [
    monitor,
    setMonitor,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(false);

  // ============================================================
  // HISTORY
  // ============================================================

  const [
    history,
    setHistory,
  ] = useState({
    range: '',
    window_days: 0,
    uptime_pct: 0,
    total_checks: 0,
    series: [],
    checks: [],
  });

  const [
    historyLoading,
    setHistoryLoading,
  ] = useState(false);

  const [
    historyLoaded,
    setHistoryLoaded,
  ] = useState(false);

  // ============================================================
  // INCIDENTS
  // ============================================================

  const [
    incidents,
    setIncidents,
  ] = useState([]);

  const [
    incidentsLoading,
    setIncidentsLoading,
  ] = useState(false);

  const [
    incidentsLoaded,
    setIncidentsLoaded,
  ] = useState(false);

  // ============================================================
  // RESPONSE HELPER
  // ============================================================

  const unwrapResponse = (
    response,
  ) =>
    response?.data?.data ||
    response?.data ||
    response;

  // ============================================================
  // FETCH MONITOR DETAILS
  // ============================================================

  const fetchMonitorDetails =
    useCallback(async () => {
      if (
        !projectId ||
        !monitorId
      ) {
        return;
      }

      try {
        setLoading(true);

        const response =
          await apiEndPoints.getEndpointMonitor(
            projectId,
            monitorId,
          );

        const data =
          unwrapResponse(
            response,
          );

        console.log(
          'Endpoint monitor details:',
          data,
        );

        if (
          data?.statuscode &&
          data.statuscode !== '00'
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              'Unable to fetch endpoint monitor',
          );

          return;
        }

        const monitorData =
          data?.monitor ||
          data;

        setMonitor(
          monitorData,
        );
      } catch (error) {
        console.error(
          'Unable to fetch endpoint monitor:',
          error,
        );

        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.response?.data
              ?.detail ||
            error?.message ||
            'Unable to fetch endpoint monitor',
        );
      } finally {
        setLoading(false);
      }
    }, [
      projectId,
      monitorId,
    ]);

  // ============================================================
  // FETCH HISTORY
  // ============================================================

  const fetchMonitorHistory =
    useCallback(async () => {
      if (
        !projectId ||
        !monitorId
      ) {
        return;
      }

      try {
        setHistoryLoading(
          true,
        );

        const response =
          await apiEndPoints.getEndpointMonitorHistory(
            projectId,
            monitorId,
          );

        const data =
          unwrapResponse(
            response,
          );

        console.log(
          'Endpoint monitor history:',
          data,
        );

        setHistory({
          range:
            data?.range || '',

          window_days:
            Number(
              data?.window_days ||
                0,
            ),

          uptime_pct:
            Number(
              data?.uptime_pct ||
                0,
            ),

          total_checks:
            Number(
              data?.total_checks ||
                0,
            ),

          series:
            Array.isArray(
              data?.series,
            )
              ? data.series
              : [],

          checks:
            Array.isArray(
              data?.checks,
            )
              ? data.checks
              : [],
        });
      } catch (error) {
        console.error(
          'Unable to fetch endpoint monitor history:',
          error,
        );

        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.response?.data
              ?.detail ||
            error?.message ||
            'Unable to fetch endpoint monitor history',
        );
      } finally {
        setHistoryLoading(
          false,
        );

        setHistoryLoaded(
          true,
        );
      }
    }, [
      projectId,
      monitorId,
    ]);

  // ============================================================
  // FETCH INCIDENTS
  // ============================================================

  const fetchMonitorIncidents =
    useCallback(async () => {
      if (
        !projectId ||
        !monitorId
      ) {
        return;
      }

      try {
        setIncidentsLoading(
          true,
        );

        const response =
          await apiEndPoints.getEndpointMonitorIncidents(
            projectId,
            monitorId,
          );

        const data =
          unwrapResponse(
            response,
          );

        console.log(
          'Endpoint monitor incidents:',
          data,
        );

        if (
          data?.statuscode &&
          data.statuscode !== '00'
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              'Unable to fetch endpoint monitor incidents',
          );

          setIncidents([]);

          return;
        }

        setIncidents(
          Array.isArray(
            data?.incidents,
          )
            ? data.incidents
            : [],
        );
      } catch (error) {
        console.error(
          'Unable to fetch endpoint monitor incidents:',
          error,
        );

        setIncidents([]);

        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.response?.data
              ?.detail ||
            error?.message ||
            'Unable to fetch endpoint monitor incidents',
        );
      } finally {
        setIncidentsLoading(
          false,
        );

        setIncidentsLoaded(
          true,
        );
      }
    }, [
      projectId,
      monitorId,
    ]);

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    fetchMonitorDetails();
  }, [
    fetchMonitorDetails,
  ]);

  // ============================================================
  // LAZY LOAD HISTORY / INCIDENTS
  // ============================================================

  useEffect(() => {
    if (
      activeTab === 'history' &&
      !historyLoaded
    ) {
      fetchMonitorHistory();
    }

    if (
      activeTab ===
        'incidents' &&
      !incidentsLoaded
    ) {
      fetchMonitorIncidents();
    }
  }, [
    activeTab,
    historyLoaded,
    incidentsLoaded,
    fetchMonitorHistory,
    fetchMonitorIncidents,
  ]);

  // ============================================================
  // REFRESH
  // ============================================================

  const handleRefresh =
    async () => {
      if (
        activeTab ===
        'history'
      ) {
        await fetchMonitorHistory();

        return;
      }

      if (
        activeTab ===
        'incidents'
      ) {
        await fetchMonitorIncidents();

        return;
      }

      await fetchMonitorDetails();
    };

  // ============================================================
  // HELPERS
  // ============================================================

  const displayValue = (
    value,
  ) => {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return 'N/A';
    }

    if (
      Array.isArray(value)
    ) {
      return value.length
        ? value.join(', ')
        : 'N/A';
    }

    return value;
  };

  const formatKey = (
    key,
  ) =>
    String(key)
      .replace(
        /_/g,
        ' ',
      )
      .replace(
        /\b\w/g,
        (character) =>
          character.toUpperCase(),
      );

  // ============================================================
  // STATUS BADGE
  // ============================================================

  const StatusBadge = ({
    status,
  }) => {
    const normalized =
      String(
        status || '',
      ).toLowerCase();

    if (
      normalized.includes(
        'healthy',
      ) ||
      normalized === 'up' ||
      normalized.includes(
        'active',
      ) ||
      normalized.includes(
        'success',
      ) ||
      normalized.includes(
        'resolved',
      )
    ) {
      return (
        <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
          {status || 'N/A'}
        </span>
      );
    }

    if (
      normalized.includes(
        'unhealthy',
      ) ||
      normalized === 'down' ||
      normalized.includes(
        'failed',
      ) ||
      normalized.includes(
        'error',
      ) ||
      normalized.includes(
        'open',
      )
    ) {
      return (
        <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
          {status || 'N/A'}
        </span>
      );
    }

    if (
      normalized.includes(
        'unknown',
      ) ||
      normalized.includes(
        'pending',
      )
    ) {
      return (
        <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
          {status || 'N/A'}
        </span>
      );
    }

    return (
      <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
        {status || 'N/A'}
      </span>
    );
  };

  // ============================================================
  // ACTIVE LOADING STATE
  // ============================================================

  const activeLoading =
    activeTab === 'history'
      ? historyLoading
      : activeTab ===
          'incidents'
        ? incidentsLoading
        : loading;

  // ============================================================
  // INITIAL LOADING
  // ============================================================

  if (
    loading &&
    !monitor
  ) {
    return (
      <div className="p-4 md:p-8 dark:text-white">

        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center dark:border-gray-700 dark:bg-slate-900">

          <p className="text-sm text-gray-500">
            Loading endpoint
            monitor details...
          </p>

        </div>

      </div>
    );
  }

  // ============================================================
  // NOT FOUND
  // ============================================================

  if (!monitor) {
    return (
      <div className="p-4 md:p-8 dark:text-white">

        <button
          type="button"
          onClick={() =>
            navigate(-1)
          }
          className="mb-5 text-sm font-medium text-primaryBlue hover:underline"
        >
          ← Back
        </button>

        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center dark:border-gray-700 dark:bg-slate-900">

          <p className="text-sm text-gray-500">
            Endpoint monitor
            details could not be
            found.
          </p>

        </div>

      </div>
    );
  }

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div className="grid gap-6 p-4 md:p-8 dark:text-white">

      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

        <div>

          <button
            type="button"
            onClick={() =>
              navigate(-1)
            }
            className="mb-3 text-sm font-medium text-primaryBlue hover:underline"
          >
            ← Back to Endpoint
            Monitoring
          </button>

          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Endpoint Monitor
            Details
          </h1>

          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View endpoint health,
            monitoring history and
            incident reports.
          </p>

        </div>

        <button
          type="button"
          onClick={
            handleRefresh
          }
          disabled={
            activeLoading
          }
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-slate-800"
        >
          {activeLoading
            ? 'Refreshing...'
            : 'Refresh'}
        </button>

      </div>

      {/* ===================================================== */}
      {/* MONITOR SUMMARY */}
      {/* ===================================================== */}

      <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-slate-900">

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

          <div>

            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
              Endpoint
            </p>

            <p className="mt-1 break-all text-base font-semibold text-gray-900 dark:text-white">
              {displayValue(
                monitor.target,
              )}
            </p>

          </div>

          <StatusBadge
            status={
              monitor.status ||
              'unknown'
            }
          />

        </div>

        <div className="mt-5 grid gap-5 border-t border-gray-100 pt-5 sm:grid-cols-2 lg:grid-cols-4 dark:border-gray-700">

          <DetailItem
            label="Monitor ID"
            value={displayValue(
              monitor.id,
            )}
          />

          <DetailItem
            label="Project ID"
            value={displayValue(
              projectId,
            )}
          />

          <DetailItem
            label="Check Type"
            value={displayValue(
              monitor.check_type,
            )}
          />

          <DetailItem
            label="Enabled"
            value={
              monitor.is_enabled
                ? 'Enabled'
                : 'Disabled'
            }
          />

        </div>

      </div>

      {/* ===================================================== */}
      {/* TABS */}
      {/* ===================================================== */}

      <div className="border-b border-gray-200 dark:border-gray-700">

        <div className="flex gap-6 overflow-x-auto">

          <TabButton
            active={
              activeTab ===
              'overview'
            }
            onClick={() =>
              setActiveTab(
                'overview',
              )
            }
          >
            Overview
          </TabButton>

          <TabButton
            active={
              activeTab ===
              'history'
            }
            onClick={() =>
              setActiveTab(
                'history',
              )
            }
          >
            History
          </TabButton>

          <TabButton
            active={
              activeTab ===
              'incidents'
            }
            onClick={() =>
              setActiveTab(
                'incidents',
              )
            }
          >
            Incidents
          </TabButton>

        </div>

      </div>

      {/* ===================================================== */}
      {/* OVERVIEW TAB */}
      {/* ===================================================== */}

      {activeTab ===
        'overview' && (
        <>

          {/* CONFIGURATION + NOTIFICATIONS */}

          <div className="grid gap-6 lg:grid-cols-2">

            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-slate-900">

              <h2 className="mb-5 font-medium text-gray-900 dark:text-white">
                Health Check
                Configuration
              </h2>

              <div className="grid gap-5 sm:grid-cols-2">

                <DetailItem
                  label="Check Type"
                  value={displayValue(
                    monitor.check_type,
                  )}
                />

                <DetailItem
                  label="Port"
                  value={displayValue(
                    monitor.port,
                  )}
                />

                <DetailItem
                  label="Expected Status Code"
                  value={displayValue(
                    monitor.expected_status_code,
                  )}
                />

                <DetailItem
                  label="Check Interval"
                  value={
                    monitor.check_interval_seconds
                      ? `${monitor.check_interval_seconds} seconds`
                      : 'N/A'
                  }
                />

                <DetailItem
                  label="Failure Threshold"
                  value={displayValue(
                    monitor.failure_threshold,
                  )}
                />

                <DetailItem
                  label="Monitor Enabled"
                  value={
                    monitor.is_enabled
                      ? 'Enabled'
                      : 'Disabled'
                  }
                />

              </div>

            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-slate-900">

              <h2 className="mb-5 font-medium text-gray-900 dark:text-white">
                Notifications
              </h2>

              <div className="grid gap-5">

                <DetailItem
                  label="Notification Email"
                  value={displayValue(
                    monitor.notify_email,
                  )}
                />

                <DetailItem
                  label="CC Email"
                  value={displayValue(
                    monitor.email_ccs,
                  )}
                />

              </div>

            </div>

          </div>

          {/* HEALTH STATS */}

          <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-slate-900">

            <h2 className="mb-5 font-medium text-gray-900 dark:text-white">
              Health Check
              Statistics
            </h2>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

              <StatCard
                label="Total Checks"
                value={displayValue(
                  monitor.total_checks,
                )}
              />

              <StatCard
                label="Total Failures"
                value={displayValue(
                  monitor.total_failures,
                )}
              />

              <StatCard
                label="Consecutive Failures"
                value={displayValue(
                  monitor.consecutive_failures,
                )}
              />

              <StatCard
                label="Last Status Code"
                value={displayValue(
                  monitor.last_status_code,
                )}
              />

            </div>

          </div>

          {/* RUNTIME */}

          <div className="grid gap-6 lg:grid-cols-2">

            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-slate-900">

              <h2 className="mb-5 font-medium text-gray-900 dark:text-white">
                Runtime Information
              </h2>

              <div className="grid gap-5">

                <DetailItem
                  label="Last Checked"
                  value={displayValue(
                    monitor.last_checked_at,
                  )}
                />

                <DetailItem
                  label="Last Up"
                  value={displayValue(
                    monitor.last_up_at,
                  )}
                />

                <DetailItem
                  label="Down Since"
                  value={displayValue(
                    monitor.down_since,
                  )}
                />

                <DetailItem
                  label="Last Error"
                  value={displayValue(
                    monitor.last_error,
                  )}
                />

              </div>

            </div>

            {/* AUDIT */}

            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-slate-900">

              <h2 className="mb-5 font-medium text-gray-900 dark:text-white">
                Audit Information
              </h2>

              <div className="grid gap-5">

                <DetailItem
                  label="Resource"
                  value={displayValue(
                    monitor.resource,
                  )}
                />

                <DetailItem
                  label="Created"
                  value={displayValue(
                    monitor.created_on,
                  )}
                />

                <DetailItem
                  label="Last Updated"
                  value={displayValue(
                    monitor.updated_on,
                  )}
                />

              </div>

            </div>

          </div>

        </>
      )}

      {/* ===================================================== */}
      {/* HISTORY TAB */}
      {/* ===================================================== */}

      {activeTab ===
        'history' && (

        <div className="grid gap-6">

          {/* HISTORY SUMMARY */}

          <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-slate-900">

            <div className="mb-5">

              <h2 className="font-medium text-gray-900 dark:text-white">
                Monitoring History
              </h2>

              <p className="mt-1 text-xs text-gray-500">
                Endpoint performance
                during the monitored
                period.
              </p>

            </div>

            {historyLoading ? (

              <LoadingState
                message="Loading endpoint history..."
              />

            ) : (

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">

                <StatCard
                  label="Range"
                  value={
                    history.range ||
                    'N/A'
                  }
                />

                <StatCard
                  label="Window"
                  value={`${history.window_days} day${
                    history.window_days ===
                    1
                      ? ''
                      : 's'
                  }`}
                />

                <StatCard
                  label="Uptime"
                  value={`${history.uptime_pct}%`}
                />

                <StatCard
                  label="Total Checks"
                  value={
                    history.total_checks
                  }
                />

              </div>

            )}

          </div>

          {/* ================================================= */}
          {/* TIME SERIES */}
          {/* ================================================= */}

          <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-slate-900">

            <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">

              <h2 className="font-medium text-gray-900 dark:text-white">
                Uptime Time Series
              </h2>

              <p className="mt-1 text-xs text-gray-500">
                Aggregated endpoint
                checks grouped by
                time.
              </p>

            </div>

            {historyLoading ? (

              <LoadingState
                message="Loading history..."
              />

            ) : history.series
                .length === 0 ? (

              <EmptyState
                title="No time-series data"
                description="No monitoring data is available for this period."
              />

            ) : (

              <div className="overflow-x-auto">

                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">

                  <thead className="bg-gray-50 dark:bg-slate-800">

                    <tr>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Time
                      </th>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Checks
                      </th>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Up
                      </th>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Uptime
                      </th>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Avg Latency
                      </th>

                    </tr>

                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">

                    {history.series.map(
                      (
                        item,
                        index,
                      ) => (

                        <tr
                          key={
                            item?.bucket ||
                            index
                          }
                          className="hover:bg-gray-50 dark:hover:bg-slate-800/50"
                        >

                          <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                            {item?.bucket ||
                              'N/A'}
                          </td>

                          <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                            {item?.checks ??
                              0}
                          </td>

                          <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                            {item?.up ??
                              0}
                          </td>

                          <td className="px-5 py-4">

                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                                Number(
                                  item?.uptime_pct ||
                                    0,
                                ) >= 100
                                  ? 'bg-green-100 text-green-700'
                                  : Number(
                                        item?.uptime_pct ||
                                          0,
                                      ) >
                                      0
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {Number(
                                item?.uptime_pct ||
                                  0,
                              )}
                              %
                            </span>

                          </td>

                          <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                            {item?.avg_latency_ms !==
                              null &&
                            item?.avg_latency_ms !==
                              undefined
                              ? `${item.avg_latency_ms} ms`
                              : 'N/A'}
                          </td>

                        </tr>

                      ),
                    )}

                  </tbody>

                </table>

              </div>

            )}

          </div>

          {/* ================================================= */}
          {/* INDIVIDUAL CHECKS */}
          {/* ================================================= */}

          <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-slate-900">

            <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">

              <h2 className="font-medium text-gray-900 dark:text-white">
                Check History
              </h2>

              <p className="mt-1 text-xs text-gray-500">
                Individual endpoint
                checks recorded
                during this period.
              </p>

            </div>

            {historyLoading ? (

              <LoadingState
                message="Loading checks..."
              />

            ) : history.checks
                .length === 0 ? (

              <EmptyState
                title="No checks found"
                description="No individual health checks have been recorded."
              />

            ) : (

              <div className="overflow-x-auto">

                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">

                  <thead className="bg-gray-50 dark:bg-slate-800">

                    <tr>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Checked At
                      </th>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status Code
                      </th>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Latency
                      </th>

                      <th className="px-5 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Error
                      </th>

                    </tr>

                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">

                    {history.checks.map(
                      (
                        check,
                        index,
                      ) => (

                        <tr
                          key={
                            check?.id ||
                            `${check?.checked_at}-${index}`
                          }
                          className="hover:bg-gray-50 dark:hover:bg-slate-800/50"
                        >

                          <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                            {check?.checked_at ||
                              'N/A'}
                          </td>

                          <td className="px-5 py-4">

                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                                check?.is_up
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {check?.is_up
                                ? 'Up'
                                : 'Down'}
                            </span>

                          </td>

                          <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                            {check?.status_code ??
                              'N/A'}
                          </td>

                          <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                            {check?.latency_ms !==
                              null &&
                            check?.latency_ms !==
                              undefined
                              ? `${check.latency_ms} ms`
                              : 'N/A'}
                          </td>

                          <td className="max-w-[450px] px-5 py-4 text-sm">

                            {check?.error ? (

                              <span
                                title={
                                  check.error
                                }
                                className="block max-w-[450px] truncate text-red-500"
                              >
                                {
                                  check.error
                                }
                              </span>

                            ) : (

                              <span className="text-gray-400">
                                —
                              </span>

                            )}

                          </td>

                        </tr>

                      ),
                    )}

                  </tbody>

                </table>

              </div>

            )}

          </div>

        </div>

      )}

      {/* ===================================================== */}
      {/* INCIDENTS TAB */}
      {/* ===================================================== */}

      {activeTab ===
        'incidents' && (

        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-slate-900">

          <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">

            <h2 className="font-medium text-gray-900 dark:text-white">
              Incident Reports
            </h2>

            <p className="mt-1 text-xs text-gray-500">
              Availability and
              downtime incidents
              detected for this
              endpoint.
            </p>

          </div>

          {incidentsLoading ? (

            <LoadingState
              message="Loading incidents..."
            />

          ) : incidents.length ===
            0 ? (

            <EmptyState
              title="No incidents found"
              description="There are currently no incident reports for this endpoint."
            />

          ) : (

            <div className="grid gap-4 p-5">

              {incidents.map(
                (
                  incident,
                  index,
                ) => (

                  <IncidentCard
                    key={
                      incident?.id ||
                      index
                    }
                    incident={
                      incident
                    }
                    index={
                      index
                    }
                    formatKey={
                      formatKey
                    }
                    displayValue={
                      displayValue
                    }
                  />

                ),
              )}

            </div>

          )}

        </div>

      )}

    </div>
  );
};

// ============================================================
// TAB BUTTON
// ============================================================

const TabButton = ({
  active,
  onClick,
  children,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
        active
          ? 'border-primaryBlue text-primaryBlue'
          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {children}
    </button>
  );
};

// ============================================================
// DETAIL ITEM
// ============================================================

const DetailItem = ({
  label,
  value,
}) => {
  return (
    <div>

      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>

      <p className="mt-1 break-words text-sm font-medium text-gray-800 dark:text-gray-200">
        {value}
      </p>

    </div>
  );
};

// ============================================================
// STAT CARD
// ============================================================

const StatCard = ({
  label,
  value,
}) => {
  return (
    <div className="rounded-lg bg-gray-50 p-4 dark:bg-slate-800">

      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>

      <p className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
        {value}
      </p>

    </div>
  );
};

// ============================================================
// INCIDENT CARD
// ============================================================

const IncidentCard = ({
  incident,
  index,
  formatKey,
  displayValue,
}) => {
  const entries =
    Object.entries(
      incident || {},
    );

  return (
    <div className="rounded-lg border border-gray-200 p-5 dark:border-gray-700">

      <div className="mb-4 flex items-center justify-between gap-4">

        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Incident{' '}
          {index + 1}
        </h3>

        {incident?.status && (
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-slate-800 dark:text-gray-300">
            {
              incident.status
            }
          </span>
        )}

      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">

        {entries.map(
          ([
            key,
            value,
          ]) => (

            <div
              key={key}
              className={
                key ===
                  'error' ||
                key ===
                  'message'
                  ? 'sm:col-span-2 lg:col-span-3'
                  : ''
              }
            >

              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                {formatKey(
                  key,
                )}
              </p>

              <p className="mt-1 break-words text-sm text-gray-800 dark:text-gray-200">
                {displayValue(
                  value,
                )}
              </p>

            </div>

          ),
        )}

      </div>

    </div>
  );
};

// ============================================================
// LOADING STATE
// ============================================================

const LoadingState = ({
  message,
}) => {
  return (
    <div className="p-10 text-center">

      <p className="text-sm text-gray-500">
        {message}
      </p>

    </div>
  );
};

// ============================================================
// EMPTY STATE
// ============================================================

const EmptyState = ({
  title,
  description,
}) => {
  return (
    <div className="p-10 text-center">

      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {title}
      </p>

      <p className="mt-1 text-xs text-gray-500">
        {description}
      </p>

    </div>
  );
};

export default EndpointMonitorDetails;